/**
 * Planning Monte Carlo store.
 *
 * Holds the settings and the last finished simulation for the active engine
 * instance (Scenario x Input Scope). Two surfaces read it — the Monte Carlo
 * page that runs it, and the Above / Below view that shows a probability
 * column beside the bands — so the result cannot live in either one's local
 * state.
 *
 * The result is keyed by instance and deliberately NOT persisted: it is a
 * function of the engine input plus a seed, so it is cheaper to re-run than to
 * store, and a stale probability sitting next to fresh inputs is worse than no
 * probability at all. Changing the scenario or the scope clears it.
 */

import { create } from 'zustand';
import {
  buildEngineInputScoped,
  engineInstanceKey,
  type EngineInputSource,
} from '../../lib/planningEngineService';
import {
  DEFAULT_MONTE_CARLO_CONFIG,
  MonteCarloRun,
  type MonteCarloConfig,
  type MonteCarloResult,
} from '../../lib/planningMonteCarlo';
import type { Scenario } from '../../lib/planningEngineModule.types';
import { useItemsStore } from './itemsStore';
import { useItemRelationshipsStore } from './itemRelationshipsStore';

/**
 * Trials per slice before yielding to the browser.
 *
 * Small enough that a frame is never held for long on a big portfolio, large
 * enough that the yield overhead does not dominate on a small one.
 */
const BATCH_SIZE = 25;

const yieldToBrowser = () =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });

export interface MonteCarloRunArgs {
  projectId: string;
  scenario: Scenario;
  engineInput: EngineInputSource;
  /** null = all live items; non-null Set = only those IDs. */
  scopedItemIds: Set<string> | null;
}

interface PlanningMonteCarloState {
  config: MonteCarloConfig;
  /** The engine instance `result` belongs to, or null when nothing has run. */
  instanceKey: string | null;
  result: MonteCarloResult | null;
  running: boolean;
  /** 0–1 while a simulation is in flight. */
  progress: number;
  error: string | null;

  setConfig: (patch: Partial<MonteCarloConfig>) => void;
  resetConfig: () => void;
  /** Drops a result that no longer describes the instance being looked at. */
  clearIfStale: (instanceKey: string | null) => void;
  clear: () => void;
  run: (args: MonteCarloRunArgs) => Promise<void>;
  cancel: () => void;
}

/**
 * Cancellation token. A run in flight checks it between batches; bumping it
 * abandons the run without leaving a half-finished result behind.
 */
let runToken = 0;

export const usePlanningMonteCarloStore = create<PlanningMonteCarloState>((set, get) => ({
  config: { ...DEFAULT_MONTE_CARLO_CONFIG },
  instanceKey: null,
  result: null,
  running: false,
  progress: 0,
  error: null,

  setConfig: (patch) => {
    // Settings changed means the standing result describes a different
    // question. Keep it on screen — the config panel says what it was run
    // with — but a caller checking staleness gets the honest answer.
    set((state) => ({ config: { ...state.config, ...patch } }));
  },

  resetConfig: () => set({ config: { ...DEFAULT_MONTE_CARLO_CONFIG } }),

  clearIfStale: (instanceKey) => {
    if (get().instanceKey && get().instanceKey !== instanceKey) {
      runToken += 1;
      set({ result: null, instanceKey: null, running: false, progress: 0, error: null });
    }
  },

  clear: () => {
    runToken += 1;
    set({ result: null, instanceKey: null, running: false, progress: 0, error: null });
  },

  cancel: () => {
    runToken += 1;
    set({ running: false, progress: 0 });
  },

  run: async ({ projectId, scenario, engineInput, scopedItemIds }) => {
    const token = (runToken += 1);
    const config = get().config;
    set({ running: true, progress: 0, error: null });

    try {
      // Same refresh the engine run does, so the simulation and the last engine
      // pass are looking at the same items rather than at different vintages.
      await Promise.all([
        useItemsStore.getState().fetchFromSupabase(),
        useItemRelationshipsStore.getState().fetchFromSupabase(),
      ]);
      if (token !== runToken) return;

      const relationships = useItemRelationshipsStore.getState();
      const input = await buildEngineInputScoped({
        projectId,
        scenario,
        items: useItemsStore.getState().items,
        edges: relationships.edges,
        scopedItemIds,
        scopeKind: engineInput.type,
        relationshipGroups: relationships.groups,
        relationshipGroupMemberships: relationships.groupMemberships,
      });
      if (token !== runToken) return;

      const run = new MonteCarloRun(input, config);
      while (!run.done) {
        run.runBatch(BATCH_SIZE);
        if (token !== runToken) return;
        set({ progress: run.progress });
        await yieldToBrowser();
        if (token !== runToken) return;
      }

      set({
        result: run.finalize(),
        instanceKey: engineInstanceKey(scenario.id, engineInput),
        running: false,
        progress: 1,
      });
    } catch (err) {
      if (token !== runToken) return;
      set({
        running: false,
        progress: 0,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  },
}));
